import { useTranslations } from '@/i18n/use-translations';
import type { SessionType } from '@/types/dtos/sessions/session-type.enum';
import { SESSION_TYPE } from '@/types/dtos/sessions/session-type.enum';
import { FilterList, ViewQuiltOutlined } from '@mui/icons-material';
import SessionTypeInfo from '@/components/common/session-type-info';
import { Button, Stack, Menu, MenuItem } from '@mui/material';
import { useBreakpointUp } from '@/hooks/use-breakpoint';
import { useState, useCallback } from 'react';

interface SessionsTabActionsProps {
  onViewButtonClick: () => void;
  isExpandedView?: boolean;
  setSessionType?: React.Dispatch<React.SetStateAction<SessionType | undefined>>;
  sessionType?: SessionType;
}

export const SessionsTabActions = ({
  onViewButtonClick,
  isExpandedView,
  setSessionType,
  sessionType,
}: SessionsTabActionsProps) => {
  const isDesktop = useBreakpointUp('lg');
  const t = useTranslations();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleFilterClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSessionTypeSelect = useCallback(
    (sessionType: SessionType) => {
      setSessionType?.(sessionType);
      setAnchorEl(null);
    },
    [setSessionType],
  );

  const handleClearFilter = useCallback(() => {
    setSessionType?.(undefined);
    setAnchorEl(null);
  }, [setSessionType]);

  return (
    <Stack direction="row" gap={1}>
      {isDesktop && (
        <Button color="info" onClick={onViewButtonClick} startIcon={<ViewQuiltOutlined />}>
          {isExpandedView ? t('common.list') : t('common.details')}
        </Button>
      )}
      <Button
        startIcon={<FilterList color={sessionType ? 'primary' : 'info'} />}
        color={sessionType ? 'primary' : 'info'}
        onClick={handleFilterClick}
        aria-controls={open ? 'session-type-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        {sessionType ? t(`session.types.${sessionType}`) : t('session.types.allTypes')}
      </Button>
      <Menu
        id="session-type-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'session-type-filter-button',
        }}
      >
        <MenuItem onClick={handleClearFilter}>All Types</MenuItem>
        {Object.values(SESSION_TYPE).map((sessionType) => (
          <MenuItem key={sessionType} onClick={() => handleSessionTypeSelect(sessionType)}>
            <SessionTypeInfo type={sessionType} showDescription={false} />
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
};
